import React from 'react';
import { 
  Wrench, 
  Wifi, 
  Mail, 
  Monitor, 
  Globe, 
  Database, 
  Shield, 
  Printer,
  ArrowRight
} from 'lucide-react';
import { motion } from 'framer-motion';
import { Link } from './ui/Link';

interface ServiceCardProps {
  icon: React.ReactNode;
  title: string;
  description?: string;
  ctaLabel: string;
  ctaHref: string;
  featured?: boolean;
  delay?: number;
}

const ServiceCard: React.FC<ServiceCardProps> = ({ icon, title, description, ctaLabel, ctaHref, featured = false, delay = 0 }) => {
  return (
    <motion.div
      className={`group relative backdrop-blur-sm rounded-xl card-spacing transition-all duration-300 overflow-hidden border flex flex-col h-full ${
        featured
          ? 'bg-gradient-to-b from-accent/20 to-white/[0.03] border-accent/40'
          : 'bg-white/5 hover:bg-white/10 border-white/5 hover:border-line'
      }`}
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.45, delay, ease: 'easeOut' }}
      whileHover={{ y: -6, scale: 1.01 }}
    >
      {/* Hover gradient effect */}
      <div className={`absolute -inset-0.5 bg-gradient-to-r from-accent to-accent ${featured ? 'opacity-20' : 'opacity-0 group-hover:opacity-20'} blur rounded-xl transition-opacity duration-300`}></div>
      
      <div className="relative z-10 flex flex-col h-full content-spacing">
        {featured && (
          <span className="inline-flex self-start rounded-full border border-accent-soft/40 bg-accent-strong/20 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-accent-soft">
            Le plus demandé
          </span>
        )}
        <div className="w-12 h-12 rounded-lg bg-accent-strong/10 flex items-center justify-center group-hover:bg-accent-strong/20 transition-all duration-300 text-accent-soft">
          {icon}
        </div>
        
        <h3 className="text-fg text-base font-medium leading-tight">{title}</h3>
        {description && <p className="text-fg-muted text-sm">{description}</p>}
        
        <div className="mt-auto pt-4">
          <Link
            href={ctaHref}
            className="w-full group-hover:bg-gradient-to-r group-hover:from-accent group-hover:via-accent-hover group-hover:to-accent group-hover:text-fg text-accent-soft bg-gradient-to-r from-accent/10 via-accent-hover/10 to-accent/10 rounded-lg px-4 py-2 min-h-11 text-sm font-medium transition-all duration-300 flex items-center justify-center"
          >
            <span>{ctaLabel}</span>
            <ArrowRight size={14} className="ml-1 transform group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </div>
      </div>
    </motion.div>
  );
};

const Services: React.FC = () => {
  const services = [
    {
      icon: <Wrench size={18} />,
      title: "Dépannage logiciel",
      description: "Correction rapide des bugs, erreurs et installations bloquantes pour retrouver un poste opérationnel.",
      ctaLabel: "Résoudre ce problème",
      ctaHref: "/blog/logiciel-plante-ne-souvre-pas",
      featured: true,
    },
    {
      icon: <Wifi size={18} />,
      title: "Problèmes de réseau",
      description: "Stabilisation de votre connexion internet et WiFi pour éviter les coupures et pertes de productivité.",
      ctaLabel: "Corriger mon réseau",
      ctaHref: "/blog/internet-lent-instable",
    },
    {
      icon: <Mail size={18} />,
      title: "Gestion des emails",
      description: "Récupération d'accès, synchronisation et configuration fiable de vos boîtes mail pro et perso.",
      ctaLabel: "Récupérer mes emails",
      ctaHref: "/blog/probleme-outlook-gmail",
    },
    {
      icon: <Monitor size={18} />,
      title: "Performance PC",
      description: "Optimisation complète pour accélérer votre PC et limiter les ralentissements au quotidien.",
      ctaLabel: "Optimiser mon PC",
      ctaHref: "/blog/pc-lent-windows-10-11",
    },
    {
      icon: <Globe size={18} />,
      title: "Applications web",
      description: "Accompagnement sur M365, Google Workspace et outils cloud pour un usage fluide et sans blocage.",
      ctaLabel: "Dépanner mes apps",
      ctaHref: "/blog/applications-web-m365-google-workspace",
    },
    {
      icon: <Database size={18} />,
      title: "Gestion de données",
      description: "Récupération, transfert et sauvegarde de vos données avec priorité à la sécurité et la continuité.",
      ctaLabel: "Sauver mes données",
      ctaHref: "/blog/recuperer-fichiers-supprimes",
    },
    {
      icon: <Shield size={18} />,
      title: "Sécurité informatique",
      description: "Protection proactive, suppression des menaces et durcissement de votre environnement informatique.",
      ctaLabel: "Sécuriser mon poste",
      ctaHref: "/blog/virus-malware-ordinateur",
    },
    {
      icon: <Printer size={18} />,
      title: "Support imprimantes",
      description: "Configuration et dépannage de vos imprimantes pour retrouver une impression fiable rapidement.",
      ctaLabel: "Réparer mon imprimante",
      ctaHref: "/blog/imprimante-non-detectee",
    }
  ];

  return (
    <section id="services" className="section-spacing bg-canvas border-t border-line">
      <div className="container-site">
        <div className="text-center section-title-spacing">
          <div className="section-subtitle-badge">
            <Wrench size={14} className="mr-1.5" />
            <span className="text-xs font-medium">Mes services</span>
          </div>
          <h2 className="heading-section mb-4">
            Des solutions <span className="text-accent-soft">expertes</span> pour tous vos besoins
          </h2>
          <p className="section-intro">
            Je vous accompagne pour résoudre efficacement vos problèmes informatiques.
          </p>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, index) => (
            <ServiceCard
              key={index}
              icon={service.icon}
              title={service.title}
              description={service.description}
              ctaLabel={service.ctaLabel}
              ctaHref={service.ctaHref}
              featured={service.featured}
              delay={index * 0.04}
            />
          ))}
        </div>

        <div className="mt-16 text-center">
          <Link
            href="#contact"
            className="group relative inline-flex items-center justify-center px-8 py-4 font-bold text-white transition-colors duration-200 bg-accent-strong hover:bg-accent-hover rounded-full"
          >
            <span className="relative z-10 flex items-center">
              <Wrench className="mr-2 h-5 w-5" />
              Besoin d'aide ? Contactez-moi
            </span>
            <div className="absolute inset-0 bg-gradient-to-r from-accent-hover via-accent to-accent-hover rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </Link>
          <p className="mt-4 text-fg-muted text-sm">
            Réponse en moins de 2h, intervention souvent le jour même
          </p>
        </div>
      </div>
    </section>
  );
};

export default Services;